import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";

import { ControlledJsonTextarea } from "@dashboard/shared/components/JsonTextarea";
import { MdmPolicyCommand } from "@devices/types";

interface Props {
    content: MdmPolicyCommand[];
    policyCommand: MdmPolicyCommand;
    onUpdate: (content: MdmPolicyCommand[]) => Promise<boolean>;
    isOpen: boolean;
    toggleDialog: VoidFunction;
}

type FormValues = {
    command: MdmPolicyCommand;
};

export const EditPolicyCommandDialog = (props: Props) => {
    const [error, setError] = React.useState<string | null>(null);
    const { t } = useTranslation(["modals", "common"]);

    const onSubmit = async (values: FormValues) => {
        const updatedContent = props.content.map((command) => (command === props.policyCommand ? values.command : command));

        const success = await props.onUpdate(updatedContent);
        if (success) {
            props.toggleDialog();
        }
    };

    const onChange = (value: string, form: FormApi<FormValues, Partial<FormValues>>) => {
        try {
            const command: MdmPolicyCommand = JSON.parse(value);
            form.restart({ command });
            setError(null);
        } catch (_error) {
            setError(t("modals:editPolicyRule.toast.invalidJsonFormat"));
        }
    };

    return (
        <Form<FormValues>
            validateOnBlur
            onSubmit={onSubmit}
            initialValues={{ command: props.policyCommand }}
            render={({ handleSubmit, submitting, values, form }) => (
                // The dialog is rendered from a table row, so the form has to stay inside the portalled modal
                // content - wrapping the modal in it would put a <form> element straight into the <tbody>.
                <Modal
                    title={t("modals:editPolicyRule.title")}
                    open={props.isOpen}
                    // The form wraps the modal, so it stays mounted after closing, and `onChange` has already
                    // made the edited value its initial value. Without resetting here, discarded edits came back
                    // the next time the same rule was opened - and could then be saved.
                    onClose={() => {
                        form.restart({ command: props.policyCommand });
                        setError(null);
                        props.toggleDialog();
                    }}
                    endButton={
                        <PrimaryButton
                            onClick={form.submit}
                            form="policyCommand"
                            loading={submitting}
                            disabled={submitting || !!error}
                            tooltipProps={{ title: t("modals:editPolicyRule.tooltip.invalidJson"), hide: !error }}
                        >
                            {t("common:button.save")}
                        </PrimaryButton>
                    }
                >
                    <form onSubmit={handleSubmit} id="policyCommand">
                        <ControlledJsonTextarea<FormValues>
                            defaultValue={values?.command}
                            error={error}
                            onChange={onChange}
                            disableResize
                            alertPosition="bottom"
                        />
                    </form>
                </Modal>
            )}
        />
    );
};
